<template>
  <div class="gg-reviews" :class="{ 'is-mobile': isMobile }" v-if="isEnable">
    <transition name="gg-reviews-pop">
      <div v-if="isOpen" class="gg-reviews__card" v-click-outside="closeCard">
        <div class="gg-reviews__header">
          <div class="gg-reviews__header-info">
            <img
              class="gg-reviews__google-logo"
              :src="require('@assets/images/widget/google/google.svg')"
              width="22"
              height="22"
              alt="Google"
            />
            <div>
              <p class="gg-reviews__title mb-0">{{ shopData.title }}</p>
              <div class="gg-reviews__overall">
                <span class="gg-reviews__avg">{{ shopData.rating }}</span>
                <RatingStar :rating="shopData.rating" />
                <span class="gg-reviews__count">({{ totalReviews }})</span>
              </div>
            </div>
          </div>
          <button type="button" class="gg-reviews__close" aria-label="close" @click="closeCard">
            <i class="fas fa-times" />
          </button>
        </div>

        <div class="gg-reviews__list">
          <div v-for="(review, index) in reviews" :key="review.reviewId" class="gg-reviews__item">
            <div class="gg-reviews__item-head">
              <ReviewAvatar
                class="gg-reviews__avatar"
                :name="reviewerName(review)"
                :photo-url="reviewerPhoto(review)"
                :background="avatarColor(index)"
                :max-initials="1"
                :is-google-user="isAnonymous(review)"
              />
              <div class="gg-reviews__item-meta">
                <p class="gg-reviews__item-name mb-0">{{ reviewerName(review) }}</p>
                <span class="gg-reviews__item-time">{{ createdAgo(review) }}</span>
              </div>
            </div>
            <RatingStar :rating="review.starRating" class="gg-reviews__item-stars" />
            <ReviewComment
              class="gg-reviews__comment"
              :comment="review.comment"
              :comment-translated="review.commentTranslated"
              :language="languageUse"
            />
          </div>
        </div>

        <a
          v-if="seeAllReviewsUri"
          class="gg-reviews__more"
          :href="seeAllReviewsUri"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t("website.widgetReview.lblSeeAllReviews", languageUse) }}
        </a>
      </div>
    </transition>

    <button
      type="button"
      class="gg-reviews__btn"
      :class="{ active: isOpen }"
      aria-label="Google reviews"
      @click="toggleCard"
    >
      <img
        class="gg-reviews__btn-icon"
        :src="require('@assets/images/widget/google/google.svg')"
        width="36"
        height="36"
        alt="Google reviews"
      />
      <span class="gg-reviews__btn-rating" v-if="shopData.rating != null">
        <i class="fas fa-star" />
        {{ shopData.rating }}
      </span>
    </button>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import RatingStar from "@sharedcomp/RatingStar/RatingStar";
import ReviewAvatar from "@sharedcomp/Review/ReviewAvatar";
import ReviewComment from "@sharedcomp/Review/ReviewComment";
import googleService from "@services/googleService.js";
import { timeAgo, isAnonymousReviewer, reviewerDisplayName, reviewerPhoto, allReviewsUri } from "@helpers/review.helper";

const AVATAR_COLORS = ["#4285F4", "#DB4437", "#0F9D58", "#F4B400"];

export default {
  components: {
    RatingStar,
    ReviewAvatar,
    ReviewComment,
  },
  directives: {
    "click-outside": {
      bind(el, binding) {
        el.__ggClickOutside__ = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        setTimeout(() => {
          document.addEventListener("click", el.__ggClickOutside__);
        }, 0);
      },
      unbind(el) {
        document.removeEventListener("click", el.__ggClickOutside__);
        delete el.__ggClickOutside__;
      },
    },
  },
  data() {
    return {
      isEnable: false,
      isOpen: false,
      windowWidth: 0,
      reviews: [],
      totalReviews: 0,
      shopData: {},
    };
  },
  computed: {
    ...mapGetters({
      preview: "designIframe/preview",
      languageUse: "designIframe/metadata/siteActiveLanguage",
    }),
    isMobile() {
      return this.windowWidth > 0 && this.windowWidth < 768;
    },
    seeAllReviewsUri() {
      return allReviewsUri(this.shopData.newReviewUri);
    },
  },
  methods: {
    async getReviews() {
      try {
        const response = await googleService.getGoogleReviews();
        this.isEnable = Number(response && response.isEnable) === 1;
        this.reviews = (response && response.reviews) || [];
        this.totalReviews = Number(response && response.totalReviews) || 0;
        this.shopData = (response && response.data) || {};
      } catch (error) {
        this.isEnable = false;
      }
    },
    toggleCard() {
      if (!this.preview) return;
      this.isOpen = !this.isOpen;
    },
    closeCard() {
      this.isOpen = false;
    },
    reviewerName(review) {
      return reviewerDisplayName(review, this.$t("website.widgetReview.lblGoogleUser", this.languageUse));
    },
    reviewerPhoto(review) {
      return reviewerPhoto(review);
    },
    isAnonymous(review) {
      return isAnonymousReviewer(review);
    },
    avatarColor(index) {
      return AVATAR_COLORS[index % AVATAR_COLORS.length];
    },
    createdAgo(review) {
      return timeAgo(review.createdTime);
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
  },
  watch: {
    preview(value) {
      if (!value) this.isOpen = false;
    },
  },
  mounted() {
    this.getReviews();
    this.windowWidth = window.innerWidth;
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
};
</script>

<style lang="scss">
.gg-reviews {
  $accent: #4285f4;

  &__btn {
    position: fixed;
    bottom: 24px;
    left: 24px;
    z-index: 100;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px 8px 10px;
    background: #ffffff;
    border: 1px solid #e6e6e6;
    border-radius: 40px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    transition: bottom 0.2s ease-in-out, left 0.2s ease-in-out,
      box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out;

    &:hover {
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
      transform: translateY(-1px);
    }

    &-icon {
      display: block;
    }

    &-rating {
      font-size: 18px;
      font-weight: 600;
      color: #3c4043;
      white-space: nowrap;

      .fa-star {
        color: #f8cf5b;
        font-size: 18px;
        margin-right: 2px;
      }
    }
  }

  &__card {
    position: fixed;
    bottom: 84px;
    left: 24px;
    z-index: 101;
    width: 340px;
    max-width: calc(100vw - 32px);
    max-height: 70vh;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
    overflow: hidden;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;

    &-info {
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }

  &__google-logo {
    display: block;
    flex-shrink: 0;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: #202124;
    line-height: 1.2;
  }

  &__overall {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
  }

  &__avg {
    font-size: 14px;
    font-weight: 700;
  }

  &__count {
    font-size: 12px;
    color: #70757a;
  }

  &__close {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    background: transparent;
    border: none;
    border-radius: 50%;
    color: #70757a;
    cursor: pointer;
    transition: background 0.15s ease-in-out;

    &:hover {
      background: #f1f3f4;
    }
  }

  &__list {
    flex: 1;
    overflow-y: auto;
    padding: 4px 0;
  }

  &__item {
    padding: 12px 16px;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
      border-bottom: none;
    }

    &-head {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 6px;
    }

    &-meta {
      display: flex;
      flex-direction: column;
    }

    &-name {
      font-size: 14px;
      font-weight: 600;
      color: #202124;
      line-height: 1.2;
    }

    &-time {
      font-size: 11px;
      color: #70757a;
    }

    &-stars {
      margin-bottom: 4px;
    }
  }

  &__avatar {
    --review-avatar-size: 36px;
    --review-avatar-fs: 16px;
    --review-avatar-color: #ffffff;
  }

  &__comment {
    --review-comment-fs: 13px;
    --review-comment-link-fs: 12px;
    --review-link-color: #{$accent};
    --review-comment-muted: #70757a;
  }

  &__more {
    display: block;
    padding: 12px 16px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: $accent;
    border-top: 1px solid #f0f0f0;
    text-decoration: none;

    &:hover {
      background: #f8f9fa;
      color: $accent;
      text-decoration: none;
    }
  }

  &.is-mobile {
    .gg-reviews__btn {
      bottom: 16px;
      left: 16px;
      padding: 7px 12px 7px 9px;
    }

    .gg-reviews__card {
      left: 16px;
      bottom: 72px;
      width: calc(100vw - 32px);
    }
  }
}

.gg-reviews-pop-enter-active,
.gg-reviews-pop-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.gg-reviews-pop-enter,
.gg-reviews-pop-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
